import React, { useState } from 'react';
import './OldBloodTestTable.scss';
import TableRowActionMenu from '../../BaseComponents/TableRowActionMenu';
import { BloodTestData, downloadMemberBloodTest, markBloodTestAsComplete,  } from '../../../api/oldBloodTest';
import { useLocation } from 'react-router-dom';
import BiomarkerExtractionModal from '../BiomarkerExtractionModal';
import { parseBloodReportByFilename } from '../../../api/corevitals';
import { ParsedBiomarker } from '../../../api/corevitals';
import Loader from '../../BaseComponents/Loader';
import { toast } from 'react-toastify';

interface IProps {
  bloodTestData: BloodTestData;
  onRefresh:()=>void;
  showSelectCheckbox?: boolean;
  isSelected?: boolean;
  onSelectedChange?: (id: number, nextSelected: boolean) => void;
}

const OldBloodTestTableRow: React.FC<IProps> = ({
  bloodTestData,
  onRefresh,
  showSelectCheckbox = false,
  isSelected = false,
  onSelectedChange
}) => {
  const location = useLocation();
  const [isExtractionModalOpen, setIsExtractionModalOpen] = useState(false);
  const [parsedBiomarkers, setParsedBiomarkers] = useState<ParsedBiomarker[]>([]);
  const [extractionSummary, setExtractionSummary] = useState<Record<string, any> | undefined>(undefined);
  const [extractionResponseData, setExtractionResponseData] = useState<Record<string, any> | undefined>(undefined);
  const [missingList, setMissingList] = useState<Array<{ biomarker_name: string; standard_unit: string }>>([]);
  const [isExtracting, setIsExtracting] = useState(false);
  const [isMarkingComplete,setIsMarkingComplete]=useState(false);
  const [currentStatus,setCurrentStatus]=useState<BloodTestData['status']>(bloodTestData.status)
  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch (error) {
      return dateString;
    }
  };

  const handleExtractReportData = async () => {
    try {
      setIsExtracting(true);
      const response = await parseBloodReportByFilename(
        bloodTestData.file_name,
        bloodTestData.file_path
      );

      if (response.success && ((response.data as any)?.matched_biomarkers || response.data?.biomarkers)) {
        // Handle both matched_biomarkers (new format) and biomarkers (old format)
        const data = response.data as any;
        const biomarkers = data.matched_biomarkers || response.data.biomarkers || [];
        setParsedBiomarkers(biomarkers);
        setExtractionSummary(response.data.summary);
        setExtractionResponseData(response.data);
        setMissingList(data.missing_list || []);
        setIsExtractionModalOpen(true);
      } else {
        alert(response.error || 'Failed to extract biomarkers from the report');
      }
    } catch (error: any) {
      console.error('Extraction error:', error);
      const errorMessage = error?.response?.data?.error || 
                          error?.message || 
                          'Failed to extract report data. Please try again.';
      alert(errorMessage);
    } finally {
      setIsExtracting(false);
    }
  };

  const onActionClick = async (id: number, action: string) => {
    try {
      const memberId = location.pathname.split('/')[2];
      
      if (!memberId) {
        return;
      }

      if (action === 'download') {
        const presignedUrl = await downloadMemberBloodTest(memberId, id);
        
        const a = document.createElement('a');
        a.href = presignedUrl;
        a.download = bloodTestData.file_name;
        a.target = '_blank';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } else if (action === 'extract') {
        await handleExtractReportData();
      }else if (action === 'mark-complete') {
        setIsMarkingComplete(true);
        try {
          const updatedData = await markBloodTestAsComplete(id);
          setCurrentStatus(updatedData.status);
          toast.success("Blood Test Completed")
          if (onRefresh) {
            onRefresh();
          }
        } catch (error: any) {
          const errorMessage = error?.response?.data?.error ||
            error?.message ||
            'Failed to mark blood test as complete. Please try again.';
          alert(errorMessage);
        } finally {
          setIsMarkingComplete(false);
        }
      }
    } catch (error) {
      alert('Failed to perform action. Please try again.');
    }
  };

  const memberId = location.pathname.split('/')[2];

  return (
    <>
      <div className={`table-row old-blood-test-table-row ${isExtracting ? 'extracting' : ''}`}>
        <div className='select-cell'>
          {showSelectCheckbox ? (
            <input
              type='checkbox'
              checked={isSelected}
              onChange={(e) => onSelectedChange?.(bloodTestData.id, e.target.checked)}
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <span className='select-placeholder' />
          )}
        </div>
        <div className='test-date'>{formatDate(bloodTestData.upload_date)}</div>
        <div className='test-name'>{bloodTestData.file_name}</div>
        <div className='test-status'>
          {currentStatus === 'COMPLETED' ? (
            <span className='status completed'>COMPLETED</span>
          ) : (
            <span className='status uploaded'>UPLOADED</span>
          )}
        </div>
        <div className='actions'>
          <TableRowActionMenu
            options={[
              { key: 'download', value: 'Download' },
              { key: 'extract', value: isExtracting ? 'Extracting...' : 'Extract Report Data' },
              ...(currentStatus !== 'COMPLETED' ? [
                { key: 'mark-complete', value: isMarkingComplete ? 'Marking as Complete...' : 'Mark as Complete' }
              ] : [])
            ]}
            id={bloodTestData.id}
            onActionMenuClick={onActionClick}
          />
        </div>
        {isExtracting && (
          <div className='extraction-loading-overlay'>
            <Loader />
            <span className='extraction-loading-text'>Extracting report data...</span>
          </div>
        )}
      </div>

      <BiomarkerExtractionModal
        isOpen={isExtractionModalOpen}
        onClose={() => setIsExtractionModalOpen(false)}
        userId={memberId}
        parsedBiomarkers={parsedBiomarkers}
        summary={extractionSummary}
        responseData={extractionResponseData}
        missingList={missingList}
        onSaveSuccess={() => {
          setIsExtractionModalOpen(false);
          // Optionally refresh the list or show a success message
        }}
      />
    </>
  );
};

export default OldBloodTestTableRow;
